import React, { useCallback, useEffect, useState, type FunctionComponent } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Button, message, Modal, Upload } from "antd";
import ImgCrop from "antd-img-crop";
import { type RcFile } from "antd/lib/upload";
import { type UploadFile } from "antd/lib/upload/interface";
import { useTranslation } from "react-i18next";

import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";
import { normalizeFileName } from "@fitness-app/utils/src/upload/normalizeFileName";

import { supabase } from "~/store/initializeStore";
import { Logger } from "~/utils/Logger";

export interface RcCustomRequestOptions {
  onProgress: (
    event: {
      percent: number;
    },
    file: RcFile,
  ) => void;
  onError: (error: Error, response?: unknown, file?: RcFile) => void;
  onSuccess: (response: object, file: RcFile) => void;
  data: object;
  filename: string;
  file: RcFile;
  withCredentials: boolean;
  action: string;
  headers: object;
}

interface OwnProps {
  storageRef: string;
  /** Complete list of URLs the field now holds — empty after the last file was removed. */
  onChange?: (urls: string[], files: UploadFile<File>[]) => void;
  fileList?: string[] | [{ url: string }];
  multiple?: boolean;
  withCrop?: boolean;
  disabled?: boolean;
  fileName?: string;
  idAsName?: boolean;
  bucket?: string;
  icon?: typeof UploadOutlined;
  className?: string;
}

type Props = OwnProps;

const EXPIRES_IN_20_YEARS = 631138519;

const beforeUpload = (file: RcFile) => {
  const validationType =
    file.type === "image/jpeg" ||
    file.type === "image/jpg" ||
    file.type === "image/gif" ||
    file.type === "image/webp" ||
    file.type === "image/png";
  if (!validationType) {
    void message.error("Rozszerzenie pliku jest niedopuszczalne, wyślij zdjęcie w formacie jpg/gif/png");
  }
  const isLt1M = file.size / 1024 / 1024 < 1;
  if (!isLt1M) {
    void message.error("Zdjęcie nie może być większe niż 1MB");
  }

  return validationType && isLt1M;
};

interface File extends RcFile {
  url: string | null;
}

const getExtension = (name: string) => {
  const extension = name.split(".").pop();

  return extension && extension !== name ? normalizeFileName(extension.toLowerCase()) : "jpg";
};

/**
 * Object path a storage URL points at, or `null` when the URL belongs to another bucket.
 * Public, render and signed URLs all embed it right after the bucket segment.
 */
const extractObjectPath = (url: string, bucket: string) => {
  const marker = `/${bucket}/`;
  const start = url.indexOf(marker);

  if (start === -1) {
    return null;
  }

  const path = url.slice(start + marker.length).split("?")[0] ?? "";

  try {
    return decodeURIComponent(path) || null;
  } catch {
    return path || null;
  }
};

/**
 * users-service resizes every freshly saved avatar into a `thumb_`-prefixed sibling and repoints
 * the profile at it, so the URL we hold names only half of what the upload left behind. Removing
 * has to take both, otherwise the bucket keeps collecting orphans (TRE-1729).
 */
const getPathsToRemove = (path: string) => {
  const segments = path.split("/");
  const name = segments.pop() ?? "";
  const directory = segments.join("/");
  const sibling = name.startsWith("thumb_") ? name.slice("thumb_".length) : `thumb_${name}`;

  return [path, directory ? `${directory}/${sibling}` : sibling];
};

const buildDefaultFile = (url: string): UploadFile<File> =>
  ({
    url,
    name: "default",
    size: 0,
    uid: "-1",
    status: "done",
    type: "image/png",
  }) as UploadFile<File>;

const AvatarUploadField: FunctionComponent<Props> = ({
  storageRef,
  onChange,
  multiple,
  fileList: defaultFileList,
  withCrop = true,
  disabled = false,
  fileName,
  idAsName = false,
  icon: Icon = UploadOutlined,
  bucket = "shared",
  className,
}) => {
  const { t } = useTranslation("common");

  const first = defaultFileList?.[0];
  // Callers pass a freshly built array on every render, so the effect below has to key off the URL
  // itself — keying off the array identity re-ran it constantly and wiped the in-progress upload.
  const defaultUrl = (typeof first === "string" ? first : first?.url) || null;

  const [fileList, updateFileList] = useState<UploadFile<File>[]>(() =>
    defaultUrl ? [buildDefaultFile(defaultUrl)] : [],
  );

  useEffect(() => {
    updateFileList(defaultUrl ? [buildDefaultFile(defaultUrl)] : []);
  }, [defaultUrl]);

  const [preview, setPreviewImg] = useState<string | null>(null);

  const onPreview = useCallback(
    (file: UploadFile) => {
      setPreviewImg((file?.url || file?.thumbUrl) ?? null);
    },
    [setPreviewImg],
  );

  /**
   * A unique, sanitised object name — the same shape the mobile app uploads with. The raw
   * `file.name` used to go straight into the path, which put spaces and commas into object names
   * and, once re-encoded downstream, produced URLs that answered 400 (TRE-1729). Being unique also
   * keeps the CDN from serving the previous avatar from a path that was just overwritten.
   */
  const buildObjectPath = (file: RcFile) => {
    const base = idAsName ? file.uid : normalizeFileName(fileName || file.name.replace(/\.[^.]+$/, ""));

    return `${storageRef}/${base}_${generateUniqId()}.${getExtension(file.name)}`;
  };

  const uploadToStorage = async ({ file, onSuccess }: RcCustomRequestOptions) => {
    const uploadingFile: UploadFile<File> = {
      type: file.type,
      name: file.name,
      uid: file.uid,
      lastModified: file.lastModified,
      size: file.size,
      percent: 0,
      status: "uploading",
    };
    // Without `multiple` the field holds one file, so a new upload replaces the current one instead
    // of queuing behind it — otherwise the stale entry stays first and callers reading the list
    // keep picking up the previous avatar.
    updateFileList((prev) => (multiple ? [...prev, uploadingFile] : [uploadingFile]));

    try {
      const ref = buildObjectPath(file);

      const { error: uploadError } = await supabase.storage.from(bucket).upload(ref, file, {
        cacheControl: "3600",
        upsert: true,
      });

      if (uploadError) {
        void message.error(t("validationErrors.cannotAddImage"));
        updateFileList((prev) =>
          prev.map((savedFile) => {
            if (savedFile.uid === file.uid) {
              return {
                ...savedFile,
                error: uploadError.message,
                status: "error",
              };
            }

            return savedFile;
          }),
        );
        return;
      }

      let url = "";

      if (bucket === "shared") {
        const {
          data: { publicUrl },
        } = supabase.storage.from(bucket).getPublicUrl(ref, {
          transform: {
            width: 300,
            height: 300,
            quality: 90,
            resize: "cover",
          },
        });
        url = publicUrl;
      } else {
        const { data, error } = await supabase.storage.from(bucket).createSignedUrl(ref, EXPIRES_IN_20_YEARS, {
          transform: {
            width: 300,
            height: 300,
            quality: 90,
            resize: "cover",
          },
        });
        if (error) {
          throw new Error(error.message);
        }
        url = data.signedUrl;
      }

      onSuccess({}, file);

      const uploadedFile: UploadFile<File> = { ...uploadingFile, percent: 100, status: "done", url };
      const nextFiles = multiple ? [...fileList, uploadedFile] : [uploadedFile];

      updateFileList(nextFiles);
      onChange?.(
        nextFiles.map((item) => item.url).filter((itemUrl): itemUrl is string => Boolean(itemUrl)),
        nextFiles,
      );
    } catch (error) {
      void message.error(t("validationErrors.cannotAddImage"));
      updateFileList((prev) =>
        prev.map((savedFile) => {
          if (savedFile.uid === file.uid) {
            return {
              ...savedFile,
              status: "error",
            };
          }

          return savedFile;
        }),
      );
    }
  };

  const onRemove = async (file: UploadFile) => {
    const nextFiles = fileList.filter((savedFile) => savedFile.url !== file.url);

    updateFileList(nextFiles);
    // Single notification: the caller learns the field is empty from `onChange` alone. Firing a
    // second "deleted" callback made the consumer write the same state twice in a row (TRE-1729).
    onChange?.(
      nextFiles.map((item) => item.url).filter((url): url is string => Boolean(url)),
      nextFiles,
    );

    // The object path comes from the URL rather than from the props: the upload no longer names
    // files after `fileName`, and before TRE-1729 the two disagreed, so removal silently deleted a
    // path that had never existed while the real file stayed in the bucket.
    const path = file.url ? extractObjectPath(file.url, bucket) : null;

    if (!path) {
      return;
    }

    try {
      const { error } = await supabase.storage.from(bucket).remove(getPathsToRemove(path));

      if (error) {
        Logger.error(error);
      }
    } catch (e) {
      Logger.error(e);
    }
  };

  if (!withCrop) {
    return (
      <>
        <Upload
          name="logo"
          listType="picture-card"
          beforeUpload={beforeUpload}
          onPreview={onPreview}
          onRemove={onRemove}
          fileList={fileList}
          multiple={multiple}
          // @ts-expect-error wrong return type
          customRequest={uploadToStorage}
        >
          {!multiple && fileList.length >= 1 ? null : <Button type="text" icon={<Icon />} />}
        </Upload>
        <Modal open={!!preview} footer={null} onCancel={() => setPreviewImg(null)}>
          <img alt="example" style={{ width: "100%", marginTop: 20 }} src={preview ?? undefined} />
        </Modal>
      </>
    );
  }

  return (
    <div className={className}>
      <ImgCrop>
        <Upload
          name="logo"
          listType="picture-card"
          beforeUpload={beforeUpload}
          onPreview={onPreview}
          onRemove={onRemove}
          fileList={fileList}
          multiple={multiple}
          // @ts-expect-error wrong return type
          customRequest={uploadToStorage}
          disabled={disabled}
        >
          {!multiple && fileList.length >= 1 ? null : <Button type="text" icon={<Icon />} />}
        </Upload>
      </ImgCrop>
      <Modal open={!!preview} footer={null} onCancel={() => setPreviewImg(null)}>
        <img alt="example" style={{ width: "100%", marginTop: 15 }} src={preview ?? undefined} />
      </Modal>
    </div>
  );
};

export default AvatarUploadField;
